import React, { useState, useRef, useEffect } from 'react'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import Scrollbar from 'react-scrollbars-custom'
import { Tooltip } from 'react-tooltip'

import styles from './styles.module.scss'
import { updatePlaceDocumentAccess } from '../../../api'
import { getCheckedEmployeeIds } from '../../../helpers/getCheckedEmployeeIds'

import Dropdown from '../../Core/Dropdown/CustomDropdown'
import StyledCheckbox from '../../Core/Checkbox/Checkbox'
import Input from '../../Core/Input/Input'
import SearchIcon from '../../Icons/SearchIcon'
import QuestionIcon from '../../Icons/QuestionIcon'

const isChecked = (items) => {
  return items.every(item => {
    if (Array.isArray(item.children)) {
      return isChecked(item.children)
    }
    return Boolean(item.checked)
  })
}

const updateAllEmployees = (items, checked) => {
  return items.map(item => {
    if ("isEmployee" in item && item.isEmployee) {
      return { ...item, checked }
    }
    if ("children" in item) {
      return { ...item, children: updateAllEmployees(item.children, checked) };
    }
    return item;
  });
}

const setChecked = (items, id, checked) => {
  return items.map(item => {
    if ("isEmployee" in item && item.isEmployee) {
      if (item.id === id) {
        return { ...item, checked };
      }
      return item;
    }

    if ("isGroup" in item && item.isGroup) {
      if (item.id === id) {
        return {
          ...item,
          children: updateAllEmployees(item.children, checked)
        };
      }
      return { ...item, children: setChecked(item.children, id, checked) };
    }

    if ("isSubGroup" in item && item.isSubGroup) {
      if (item.id === id) {
        return {
          ...item,
          children: updateAllEmployees(item.children, checked)
        }
      }
      return { ...item, children: setChecked(item.children, id, checked) };
    }

    return item
  })
}

const mergeAssignedEmployees = (employees, assignedEmployees) => {
  const result = assignedEmployees.reduce((acc, assigned) => {
    return setChecked(acc, assigned.id, true)
  }, employees)
  return result
}

const filterEmployees = (employees, search) => {
  if (!search) {
    return employees
  }
  const lowerSearch = search.toLowerCase()
  const filterRecursive = (items) => {
    return items.reduce((acc, item) => {
      if (item.title.toLowerCase().includes(lowerSearch)) {
        acc.push(item)
      } else if (item.children) {
        const filteredChildren = filterRecursive(item.children)
        if (filteredChildren.length > 0) {
          acc.push({ ...item, children: filteredChildren })
        }
      }
      return acc
    }, [])
  }
  return filterRecursive(employees)
}

const FolderAccess = ({employees: initialEmployees, assignedEmployees, companyId, placeId, folderId, onChangeAccess}) => {
  const { t } = useTranslation()

  const [search, setSearch] = useState('')
  const [employees, setEmployees] = useState(initialEmployees || [])
  const searchInputRef = useRef()

  const filteredEmployees = filterEmployees(employees, search)
  const mergedEmployees = mergeAssignedEmployees(filteredEmployees, assignedEmployees)

  useEffect(() => {
    setEmployees(initialEmployees)
  }, [folderId])

  const handleInputChange = (e) => {
    setSearch(e.target.value)
  }

  const handleCheckboxChange = async (item, willCheck) => {
    const updatedEmployees = setChecked(mergedEmployees, item.id, willCheck)
    setEmployees(updatedEmployees)
    const ids = getCheckedEmployeeIds(updatedEmployees)
    const res = await updatePlaceDocumentAccess(companyId, placeId, folderId, {employee_ids: ids})
    if (res) {
      onChangeAccess()
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{ t('Users who can see this folder') }</div>
        <div 
          data-tooltip-id="access"
          data-tooltip-content={t('Select the checkbox in order to assign the user or the group / subgroup to this specific folder that they could review the documents uploaded to this folder.')}>
          <QuestionIcon />
        </div>
      </div>
      <div className={styles.contentBox}>
        <Input
          ref={searchInputRef}
          icon={<SearchIcon />}
          placeholder={t('Search')}
          value={search}
          onChange={handleInputChange}
          fullWidth
        />
        <div className={styles.checkboxGroup}>
          {/* <div className={styles.headerRow}>
            <button
              className={styles.button}
              onClick={handleSelectAll}
              disabled={countSelected === totalEmployees}
            >
              {t('Select All')}
            </button>
            <button
              className={styles.button}
              onClick={handleSelectNone}
              disabled={countSelected === 0}
            >
              {t('Select None')}
            </button>
          </div> */}
          <div className={styles.content}>
            <Scrollbar
              className={cn(styles.scrollableContent, 'styledDropdown')}
              removeTracksWhenNotUsed
              noScrollX={true}
              trackYProps={{
                renderer: ({ elementRef, ...restProps }) => (
                  <span
                    {...restProps}
                    ref={elementRef}
                    style={{...restProps.style, width: 6, right: -6}}
                  />
                ),
              }}
            >
              {
                mergedEmployees.map((item, i) => {
                  if (item.isGroup || item.isSubGroup) {
                    return (
                      <Dropdown
                        key={item.id}
                        label={item.title}
                        currentItem={item}
                        checked={isChecked(item.children)}
                        items={item.children}
                        onChange={handleCheckboxChange}
                      />
                    )
                  }
                  return (
                    <StyledCheckbox
                      key={item.id}
                      label={item.title}
                      item={item}
                      id={item.id}
                      checked={item.checked}
                      disabled={item.disabled}
                      onChange={handleCheckboxChange}
                    />
                  )
                })
              }
            </Scrollbar>
          </div>
        </div>
      </div>
      <Tooltip
        id="access"
        place="right"
        effect="solid"
        classNameArrow={styles.tooltipArrow}
        className={styles.tooltip} />
    </div>
  )
}

export default FolderAccess
